import type { Role } from "@etheria/shared";
import { fr } from "../../i18n/fr";

export interface NavLink {
  to: string;
  label: string;
  desc?: string;
  icon?: string;
  minRole?: Role;
  ownerEmail?: string;
  requiresLinked?: boolean;
}

export interface NavGroup {
  label: string;
  links: NavLink[];
}

export interface NavMenu {
  id: string;
  label: string;
  to?: string;
  icon?: string;
  links: NavLink[];
}

export const PUBLIC_MENUS: NavMenu[] = [
  {
    id: "accueil",
    label: "Accueil",
    to: "/",
    links: [
      { to: "/", label: fr.nav.accueil, desc: fr.navDesc.accueil },
      { to: "/statut", label: fr.nav.statut, desc: fr.navDesc.statut },
      { to: "/carte", label: fr.nav.carte, desc: fr.navDesc.carte },
      { to: "/guide", label: "Guide du nouveau", desc: fr.navDesc.guide },
    ],
  },
  {
    id: "actualites",
    label: "Actualités",
    to: "/patchnotes",
    links: [
      { to: "/patchnotes", label: fr.nav.patchnotes, desc: fr.navDesc.patchnotes },
      { to: "/evenements", label: "Événements", desc: "Calendrier, saisons et boss mondiaux." },
      { to: "/monde", label: fr.nav.monde, desc: fr.navDesc.monde },
      { to: "/forum", label: fr.nav.forum, desc: fr.navDesc.forum },
    ],
  },
  {
    id: "guildes",
    label: "Guildes",
    to: "/guildes",
    links: [
      { to: "/guildes", label: fr.nav.guildes, desc: fr.navDesc.guildes },
      { to: "/classements", label: fr.nav.classements, desc: fr.navDesc.classements },
      { to: "/armurerie", label: fr.nav.armurerie, desc: fr.navDesc.armurerie },
      { to: "/suggestions", label: fr.nav.suggestions, desc: fr.navDesc.suggestions },
      { to: "/signalements", label: fr.nav.signalements, desc: fr.navDesc.signalements },
    ],
  },
  {
    id: "wiki",
    label: "Wiki",
    to: "/wiki",
    links: [
      { to: "/wiki", label: fr.nav.wiki, desc: fr.navDesc.wiki },
      { to: "/wiki/metiers", label: "Métiers", desc: fr.navDesc.metiers },
      { to: "/wiki/items", label: "Objets & équipement", desc: fr.navDesc.items },
      { to: "/wiki/classes", label: "Classes", desc: "Archer, Guerrier, Mage et combat." },
      { to: "/wiki/enchants", label: "Enchantements", desc: fr.navDesc.enchants },
      { to: "/wiki/donjons", label: "Donjons", desc: fr.navDesc.donjons },
      { to: "/wiki/rng", label: fr.nav.rng, desc: fr.navDesc.rng },
      { to: "/reglement", label: "Règlement", desc: "Règles du serveur et cadre de jeu." },
    ],
  },
  {
    id: "boutique",
    label: "Boutique",
    to: "/boutique",
    links: [
      { to: "/boutique", label: "Boutique officielle", desc: fr.navDesc.boutique },
      { to: "/shop-serveur", label: "Marché du serveur", desc: "Prix, variations et dernières transactions." },
      { to: "/vote", label: fr.nav.vote, desc: fr.navDesc.vote },
    ],
  },
];

export const PUBLIC_NAV: NavLink[] = PUBLIC_MENUS.flatMap((menu) => menu.links);

export const WIKI_NAV: NavLink[] = [
  { to: "/wiki/items", label: "Objets & équipement" },
  { to: "/wiki/classes", label: "Classes" },
  { to: "/wiki/enchants", label: "Enchantements" },
  { to: "/wiki/metiers", label: "Métiers" },
  { to: "/wiki/mobs", label: "Mobs & Boss" },
  { to: "/wiki/zones", label: "Zones" },
  { to: "/wiki/donjons", label: "Donjons" },
  { to: "/wiki/crafts", label: "Artisanat" },
  { to: "/wiki/cuisine", label: "Cuisine" },
  { to: "/wiki/recolte", label: "Récolte" },
  { to: "/wiki/rng", label: "Système RNG" },
  { to: "/patchnotes", label: "Patchnotes" },
  { to: "/reglement", label: "Règlement" },
];

export const CLIENT_NAV: NavLink[] = [
  { to: "/panel", label: fr.panelNav.dashboard, minRole: "JOUEUR" },
  { to: "/panel/battlepass", label: fr.panelNav.battlepass, requiresLinked: true },
  { to: "/panel/quetes", label: fr.panelNav.quetes, requiresLinked: true },
  { to: "/panel/profil", label: fr.panelNav.profil, requiresLinked: true },
  { to: "/panel/inventaire", label: fr.panelNav.inventaire, requiresLinked: true },
  { to: "/panel/statistiques", label: fr.panelNav.statistiques, requiresLinked: true },
  { to: "/panel/historique", label: fr.panelNav.historique, requiresLinked: true },
  { to: "/panel/economie", label: fr.panelNav.economie, requiresLinked: true },
  { to: "/panel/claims", label: fr.panelNav.claims, requiresLinked: true },
  { to: "/panel/homes", label: fr.panelNav.homes, requiresLinked: true },
  { to: "/panel/guilde", label: fr.panelNav.guilde, requiresLinked: true },
  { to: "/panel/hdv", label: fr.panelNav.hdv, requiresLinked: true },
  { to: "/panel/parametres", label: fr.panelNav.parametres, minRole: "JOUEUR" },
  { to: "/panel/liaison", label: fr.panelNav.liaison, minRole: "JOUEUR" },
];

export const ADMIN_NAV: NavLink[] = [
  { to: "/admin", label: fr.adminNav.dashboard, minRole: "STAFF" },
  { to: "/admin/historique-master", label: fr.adminNav.historiqueMaster, minRole: "STAFF", ownerEmail: "contact@atelectro.fr" },
  { to: "/admin/joueurs", label: fr.adminNav.joueurs, minRole: "STAFF" },
  { to: "/admin/grades", label: fr.adminNav.grades, minRole: "ADMIN" },
  { to: "/admin/economie", label: fr.adminNav.economie, minRole: "ADMIN" },
  { to: "/admin/boutique", label: "Boutique", minRole: "ADMIN" },
  { to: "/admin/battlepass", label: "BattlePass", minRole: "ADMIN" },
  { to: "/admin/npcs", label: fr.adminNav.npcs, minRole: "ADMIN" },
  { to: "/admin/donjon-schematics", label: "Schematics DJ", minRole: "ADMIN" },
  { to: "/admin/items", label: fr.adminNav.items, minRole: "ADMIN" },
  { to: "/admin/mobs", label: fr.adminNav.mobs, minRole: "ADMIN" },
  { to: "/admin/config", label: fr.adminNav.config, minRole: "ADMIN" },
  { to: "/admin/deploiement", label: fr.adminNav.deploiement, minRole: "ADMIN" },
  { to: "/admin/contenu", label: fr.adminNav.contenu, minRole: "ADMIN" },
  { to: "/admin/forum", label: fr.adminNav.forum, minRole: "STAFF" },
  { to: "/admin/signalements", label: fr.adminNav.signalements, minRole: "STAFF" },
  { to: "/admin/suggestions", label: fr.adminNav.suggestions, minRole: "STAFF" },
  { to: "/admin/moderation", label: fr.adminNav.moderation, minRole: "MODO" },
  { to: "/admin/broadcast", label: fr.adminNav.broadcast, minRole: "MODO" },
  { to: "/admin/perf", label: fr.adminNav.perf, minRole: "STAFF" },
  { to: "/admin/logs", label: fr.adminNav.logs, minRole: "MODO" },
  { to: "/admin/carte", label: fr.adminNav.carte, minRole: "ADMIN" },
  { to: "/admin/reglages", label: fr.adminNav.reglages, minRole: "ADMIN" },
];

export function filterNav(
  links: NavLink[],
  ctx: { authenticated: boolean; linked: boolean; email?: string | null; hasRole: (min: Role) => boolean },
): NavLink[] {
  return links.filter((link) => {
    if (link.minRole && !ctx.hasRole(link.minRole)) return false;
    if (link.ownerEmail && (ctx.email ?? "").trim().toLowerCase() !== link.ownerEmail.toLowerCase()) return false;
    if (link.requiresLinked && !ctx.authenticated) return false;
    return true;
  });
}
